// eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: pre-existing write-only/unused binding in the legacy BLE+compass tree. Removing it is a behaviour question, not a lint fix; suppressed explicitly so no-unused-vars can stay an ERROR everywhere else.
import protobuf from 'protobufjs';

class ProtobufCollectionService {
  constructor(statusUuid, recordUuid, protobufType, protobufRoot, protobufProtocolRoot, protobufProtocolService) {
    this.statusUuid = statusUuid;
    this.recordUuid = recordUuid;
    this.protobufType = protobufType;
    this.protobufRoot = protobufRoot;
    this.protobufProtocolRoot = protobufProtocolRoot;
    this.protobufProtocolService = protobufProtocolService;
    
    this.onStatusChange = null;
    this.onRecordChange = null;
    this.onError = null;
    this.onProgress = null;
    
    // Progress tracking for collection loading
    this.pendingRecords = new Set();
    this.recordResolvers = new Map();
    this.recordTimeouts = new Map(); // Track timeouts for each record
    this.originalTotalRecords = 0; // Store original total for progress tracking
  }

  async initialize() {
    try {
      if (!this.protobufProtocolService) {
        throw new Error('No protobuf protocol service provided');
      }
      
      // Subscribe to status notifications through protocol service
      await this.protobufProtocolService.subscribeToCollectionStatus(
        this.statusUuid, 
        this.handleStatusChange.bind(this)
      );
      
      // Subscribe to record notifications through protocol service
      await this.protobufProtocolService.subscribeToCollectionRecord(
        this.recordUuid, 
        this.handleRecordChange.bind(this)
      );
      
      return true;
    } catch (err) {
      console.error('Collection service initialization error:', err);
      if (this.onError) {
        this.onError(`Failed to initialize collection service: ${err.message}. Status UUID: ${this.statusUuid}, Record UUID: ${this.recordUuid}`);
      }
      // Log error but don't throw to prevent React error boundary
      console.warn(`Collection service initialization failed: ${err.message}`);
      return false;
    }
  }

  handleStatusChange(status) {
    try {
      //console.log('handleStatusChange decoded status:', status);
      
      if (this.onStatusChange) {
        this.onStatusChange(status);
      }
    } catch (err) {
      console.error('Error handling status change:', err);
      if (this.onError) {
        this.onError(`Error handling status change: ${err.message}`);
      }
    }
  }

  handleRecordChange(record) {
    try {
      // Check if this is a record we're waiting for during loading
      if (this.pendingRecords.has(record.index)) {
        // eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: write-only binding (assigned later, never read). Deleting it is a behaviour change in the BLE layer, not a lint fix - audit separately.
        const RecordDataType = this.protobufRoot.lookupType(this.protobufType);
        const recordData = RecordDataType.decode(record.recordData);
        
        // Update progress immediately when record is received
        if (this.onProgress) {
          // Use the stored original total for consistent progress tracking
          const resolvedCount = this.originalTotalRecords - this.pendingRecords.size + 1; // +1 because we're about to remove this record
          this.onProgress({ current: resolvedCount, total: this.originalTotalRecords, loading: true });
        }
        
        // Also call onRecordChange to update the UI immediately
        if (this.onRecordChange) {
          this.onRecordChange(record.index, recordData);
        }
        
        // Clear the timeout for this record
        const timeoutId = this.recordTimeouts.get(record.index);
        if (timeoutId) {
          clearTimeout(timeoutId);
          this.recordTimeouts.delete(record.index);
        }
        
        // Resolve the promise for this record
        const resolver = this.recordResolvers.get(record.index);
        if (resolver && resolver.resolve) {
          resolver.resolve(recordData);
          this.recordResolvers.delete(record.index);
          this.pendingRecords.delete(record.index);
        } else {
          console.error('❌ No resolver found for record', record.index);
        }
      } else if (record.index >= 0 && record.recordData) {
        // Update existing record (normal operation) or add new record
        const RecordDataType = this.protobufRoot.lookupType(this.protobufType);
        const recordData = RecordDataType.decode(record.recordData);
        
        if (this.onRecordChange) {
          this.onRecordChange(record.index, recordData);
        }
      }
    } catch (err) {
      console.error('Error handling record change:', err);
      if (this.onError) {
        this.onError(`Error handling record change: ${err.message}`);
      }
    }
  }

  async loadCollectionStatus() {
    try {
      // Send 0,0,0,false to force device to update the characteristic
      const StatusType = this.protobufProtocolRoot.lookupType('ProtobufCollectionStatus');
      const forceUpdateStatus = StatusType.create({
        maxRecords: 0,
        currentRecords: 0,
        currentRecord: 0,
        operationSuccess: false
      });
      
      console.log('Request status update from the device');
      const status = await this.protobufProtocolService.writeCollectionStatusAndWaitForResult(this.statusUuid, forceUpdateStatus, 5000);
      
      console.log('📖 Status received from device after force update:', {
        maxRecords: status.maxRecords,
        currentRecords: status.currentRecords,
        currentRecord: status.currentRecord,
        operationSuccess: status.operationSuccess,
        fullStatus: status
      });
      
      return status;
      
    } catch (err) {
      console.error('Error loading collection status:', err);
      throw new Error(`Failed to load collection status: ${err.message}`);
    }
  }

  async clearCollection() {
    if (!this.protobufProtocolService) {
      throw new Error('No protobuf protocol service available');
    }
    
    try {
      const StatusType = this.protobufProtocolRoot.lookupType('ProtobufCollectionStatus');
      const clearStatus = StatusType.create({
        maxRecords: 0,
        currentRecords: 0,
        currentRecord: 0,
        operationSuccess: true
      });
      
      console.log('writing clear status', clearStatus);
      await this.protobufProtocolService.writeCollectionStatus(this.statusUuid, clearStatus);

      console.log('waiting for confirmation');
      // Wait for confirmation
      await this.waitForStatusConfirmation(0);
      
    } catch (err) {
      console.error('Error clearing collection:', err);
      throw new Error(`Failed to clear collection: ${err.message}`);
    }
  }

  async loadCollection(collectionStatus, onProgress) {
    if (!collectionStatus || collectionStatus.currentRecords === 0) {
      return [];
    }
    
    try {
      const totalRecords = collectionStatus.currentRecords;
      this.originalTotalRecords = totalRecords; // Store original total
      
      // Set the progress callback
      this.onProgress = onProgress;
      
      // Initialize progress
      if (onProgress) {
        onProgress({ current: 0, total: totalRecords, loading: true });
      }
      
      // eslint-disable-next-line no-unused-vars -- AUD-DEP-13-LINT: pre-existing write-only/unused binding in the legacy BLE+compass tree. Removing it is a behaviour question, not a lint fix; suppressed explicitly so no-unused-vars can stay an ERROR everywhere else.
      const RecordDataType = this.protobufRoot.lookupType(this.protobufType);
      const StatusType = this.protobufProtocolRoot.lookupType('ProtobufCollectionStatus');
      
      // Clear any existing pending records
      this.pendingRecords.clear();
      this.recordResolvers.clear();
      
      // Create promises for all records
      const recordPromises = [];
      
      for (let i = 0; i < totalRecords; i++) {
        const recordPromise = new Promise((resolve, reject) => {
          // Store both resolve and reject for this record index
          this.recordResolvers.set(i, { resolve, reject });
          
          // Add to pending records
          this.pendingRecords.add(i);
          
          // Note: Timeout will be set when the record is actually requested
        });
        
        recordPromises.push(recordPromise);
      }
      
      // Request all records and set timeouts when each is requested
      for (let i = 0; i < totalRecords; i++) {
        const readStatus = StatusType.create({
          currentRecord: i,
          currentRecords: -1,
          maxRecords: -1
        });
        
        await this.protobufProtocolService.writeCollectionStatus(this.statusUuid, readStatus);
        
        // Set timeout for this record only after it's been requested
        const timeoutId = setTimeout(() => {
          if (this.pendingRecords.has(i)) {
            this.pendingRecords.delete(i);
            const resolver = this.recordResolvers.get(i);
            this.recordResolvers.delete(i);
            this.recordTimeouts.delete(i);
            // Reject the promise
            if (resolver && resolver.reject) {
              resolver.reject(new Error(`Timeout waiting for record ${i}`));
            }
          }
        }, 5000);
        
        // Store the timeout ID so we can clear it if the record arrives
        this.recordTimeouts.set(i, timeoutId);
        
        // Small delay between requests to avoid overwhelming the device
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Wait for all records to be received (progress updates happen in handleRecordChange)
      const receivedRecords = await Promise.allSettled(recordPromises);
      
      // Check for any failed records
      const failedRecords = receivedRecords.filter(result => result.status === 'rejected');
      if (failedRecords.length > 0) {
        console.error('❌ Some records failed to load:', failedRecords);
        throw new Error(`${failedRecords.length} records failed to load: ${failedRecords.map(r => r.reason.message).join(', ')}`);
      }
      
      const successfulRecords = receivedRecords.map(result => result.value);
      
      // Final progress update
      if (onProgress) {
        onProgress({ current: totalRecords, total: totalRecords, loading: false });
      }
      
      return successfulRecords;
      
    } catch (err) {
      console.error('Error loading collection:', err);
      throw new Error(`Failed to load collection: ${err.message}`);
    }
  }

  async addItem() {
    if (!this.protobufProtocolService) {
      throw new Error('No protobuf protocol service available');
    }
    
    try {
      const StatusType = this.protobufProtocolRoot.lookupType('ProtobufCollectionStatus');
      const addStatus = StatusType.create({
        maxRecords: -1,
        currentRecords: -1,
        currentRecord: -1,
        operationSuccess: false
      });
      
      await this.protobufProtocolService.writeCollectionStatus(this.statusUuid, addStatus);
      
      // Wait for confirmation and get the new record
      await this.waitForStatusConfirmation(-1, -1);
      
      // Create a default record of the appropriate type
      const RecordDataType = this.protobufRoot.lookupType(this.protobufType);
      const newRecord = RecordDataType.create();
      
      return newRecord;
      
    } catch (err) {
      console.error('Error adding item:', err);
      throw new Error(`Failed to add item: ${err.message}`);
    }
  }

  async deleteLastItem(currentRecordsCount) {
    if (!this.protobufProtocolService) {
      throw new Error('No protobuf protocol service available');
    }
    
    try {
      const StatusType = this.protobufProtocolRoot.lookupType('ProtobufCollectionStatus');
      const deleteStatus = StatusType.create({
        maxRecords: -1,
        currentRecords: currentRecordsCount,
        currentRecord: -2, // Special value to indicate delete last
        operationSuccess: false
      });
      
      await this.protobufProtocolService.writeCollectionStatus(this.statusUuid, deleteStatus);
      
      // Wait for confirmation
      await this.waitForStatusConfirmation(currentRecordsCount - 1);
      
    } catch (err) {
      console.error('Error deleting last item:', err);
      throw new Error(`Failed to delete last item: ${err.message}`);
    }
  }

  async updateRecord(index, updatedRecord) {
    if (!this.protobufProtocolService) {
      throw new Error('No protobuf protocol service available');
    }
    
    try {
      console.log('ProtobufCollectionService.js: updateRecord called with index:', index, 'updatedRecord:', updatedRecord);
      
      // Encode the record data
      const RecordDataType = this.protobufRoot.lookupType(this.protobufType);
      const recordBuffer = RecordDataType.encode(updatedRecord).finish();
      
      // Create the record message
      const RecordType = this.protobufProtocolRoot.lookupType('ProtobufCollectionRecord');
      const recordMessage = RecordType.create({
        index: index,
        recordData: recordBuffer
      });
      
      // Write the record
      await this.protobufProtocolService.writeCollectionRecord(this.recordUuid, recordMessage);
      
      // Wait for device to confirm the operation by writing status back
      await this.waitForStatusConfirmation(null, index, 5000);
      
      //console.log('Successfully sent record update for index:', index);
      
    } catch (err) {
      console.error('Error updating record for index:', index, 'error:', err);
      throw new Error(`Failed to update record: ${err.message}`);
    }
  }

  async finishTransaction(totalRecords) {
    if (!this.protobufProtocolService) {
      throw new Error('No protobuf protocol service available');
    }
    
    try {
      console.log('finishing transaction');
      const StatusType = this.protobufProtocolRoot.lookupType('ProtobufCollectionStatus');
      const finishStatus = StatusType.create({
        maxRecords: -1,
        currentRecords: -1,
        currentRecord: -1, // Special value to indicate finish transaction
        operationSuccess: true
      });
      
      await this.protobufProtocolService.writeCollectionStatus(this.statusUuid, finishStatus);
      
      // Wait for confirmation
      await this.waitForStatusConfirmation(totalRecords, null, 10000);
      
    } catch (err) {
      console.error('Error finishing transaction:', err);
      throw new Error(`Failed to finish transaction: ${err.message}`);
    }
  }

  async uploadCollection(records, onProgress, finishTransaction) {
    if (!this.protobufProtocolService) {
      throw new Error('No protobuf protocol service available');
    }
    
    try {
      console.log('uploading collection', records.length, finishTransaction);

      // Clear existing collection first
      await this.clearCollection();
      console.log('collection cleared');
      
      // Upload each record
      for (let i = 0; i < records.length; i++) {
        await this.updateRecord(i, records[i]);
        if (onProgress) {
          onProgress({ current: i + 1, total: records.length, loading: true });
        }
      }
      
      // Finish transaction if requested
      if (finishTransaction) {
        console.log('finishing transaction');
        await this.finishTransaction(records.length);
      }
      
      if (onProgress) {
        onProgress({ current: records.length, total: records.length, loading: false });
      }
      
    } catch (err) {
      console.error('Error uploading collection:', err);
      throw new Error(`Failed to upload collection: ${err.message}`);
    }
  }

  async waitForStatusConfirmation(expectedRecords, expectedRecordIndex = null, timeoutMs = 5000) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Timeout waiting for status confirmation'));
      }, timeoutMs);
      
      const statusHandler = (status) => {
        console.log('status handler called', status);
        console.log('Waiting for - expectedRecords:', expectedRecords, 'expectedRecordIndex:', expectedRecordIndex);
        console.log('Received - currentRecords:', status.currentRecords, 'currentRecord:', status.currentRecord, 'operationSuccess:', status.operationSuccess);
        
        // Check if this is the confirmation we're waiting for
        if (expectedRecords !== null && status.currentRecords === expectedRecords) {
          console.log('✅ Matched expectedRecords:', expectedRecords, '===', status.currentRecords);
          clearTimeout(timeout);
          resolve(status);
        } else if (expectedRecordIndex !== null && status.currentRecord === expectedRecordIndex) {
          console.log('✅ Matched expectedRecordIndex:', expectedRecordIndex, '===', status.currentRecord);
          clearTimeout(timeout);
          resolve(status);
        } else if (expectedRecords === -1 && expectedRecordIndex === -1 && status.operationSuccess) {
          console.log('✅ Matched finish transaction with operationSuccess:', status.operationSuccess);
          clearTimeout(timeout);
          resolve(status);
        } else {
          reject(new Error('No conditions matched'));
        }
      };
      
      // Store original handler
      const originalHandler = this.onStatusChange;
      
      // Set temporary handler
      this.onStatusChange = statusHandler;
      
      // Override the handler to restore original after resolution
      this.onStatusChange = (status) => {
        console.log('status handler called 2', status);
        originalHandler(status);
        statusHandler(status);
        this.onStatusChange = originalHandler;
      };
    });
  }

  isConnected() {
    return this.protobufProtocolService && this.protobufProtocolService.isConnected;
  }

  disconnect() {
    // No need to manually disconnect - the protocol service handles this
    // Clear references
    this.onStatusChange = null;
    this.onRecordChange = null;
    this.onError = null;
    this.onProgress = null;
  }
}

export default ProtobufCollectionService; 